<template>
  <loading v-if="loading"></loading>
  <div class="p-dashboard" v-else>
    <page-title
      >Dashboard
      <dashboard-controls
        @save-layout="saveLayout"
        @toggle-customize="toggleCustomize"
        :edit-mode="editMode"
        v-if="!$adminDisabled"
        class="u-hide-mobile"
      />
    </page-title>

    <div class="row" v-if="affectedSystems.length > 0">
      <div class="col-xs-12 mb-12">
        <base-container
          title="System Status"
          viewLink="/system-status"
          :withGradient="false"
          header-central
          no-padding
        >
          <vuetable
            v-if="!loading"
            class="c-data-table"
            :fields="table.fields"
            :api-mode="false"
            :data="affectedSystems"
            @vuetable:row-clicked="onStatusClick"
          >
            <template slot="system-status" slot-scope="props">
              <div class="c-data-table__cell-icon">
                <system-icon :system="props.rowData"></system-icon>
              </div>
            </template>


            <template slot="actions" slot-scope="props">
              <div class="justify-between">
                <base-button is-link :primary="true" :href="`/system-status/${props.rowData.id}/status`">View Status</base-button>
                <base-button
                  v-if="!props.rowData.user_favourite && !$route.meta.isAdmin"
                  @click="onClickFollow(props.rowData.id)"
                  success
                  class="favourites--inactive"
                >
                  Follow
                </base-button>
                <base-button
                  v-else-if="
                    props.rowData.user_favourite && !$route.meta.isAdmin
                  "
                  @click="onClickUnfollow(props.rowData.id)"

                >
                  Unfollow
                </base-button>
              </div>
            </template>
          </vuetable>
        </base-container>
      </div>
    </div>

    <section class="dropzone dropzone--header">
      <div class="dropzone__container">
        <draggable
          class="dashboard--dynamic__row"
          @start="dragging = true"
          @end="dragging = false"
          :disabled="!editMode"
          handle=".handle"
          group="headerRows"
          v-model="headerLayout"
          v-bind="dragOptions"
        >
          <transition-group
            type="transition"
            :name="!dragging ? 'flip-list' : null"
          >
            <div v-for="(row, index) in headerLayout" :key="index">
              <div v-if="editMode" slot="header">
                <base-button
                  @click="deleteRow(index, 'headerLayout')"
                  ghost
                  inline
                  icon-before="delete"
                  v-if="hasNoAdminElements(index, 'headerLayout')"
                  >Delete row</base-button
                >
              </div>
              <draggable
                @start="dragging = true"
                @end="dragging = false"
                :disabled="!editMode"
                v-if="row.length"
                :group="`els${index}`"
                handle=".handle-el"
                v-model="headerLayout[index]"
                v-bind="dragOptions"
              >
                <transition-group
                  type="transition"
                  :name="!dragging ? 'flip-list' : null"
                  class="row mb-12"
                    :class="comClassName"
                  v-on:bannerLayoutOverride="layoutData"
                >

                  <div
                    v-for="(dashEl, elEindex) in row"
                    :key="elEindex"
                    :class="`col-xs-12 col-md-${dashEl.cssClass} relative`"

                  >
                    <div
                      v-if="editMode && dashEl.type"
                      class="dashboard__overlay"
                    >
                      <h2 class="text-white" v-if="dashEl.admin">
                        Widget can not be configured
                      </h2>
                      <div class="dashboard__overlay__controls" v-else>
                        <base-button
                          outline
                          success-invert
                          icon-before="move-side"
                          class="handle-el"
                          v-if="notSingleCol(index, 'headerLayout')"
                          >Move</base-button
                        >
                        <base-button
                          primary-invert
                          icon-before="cog"
                          @click="
                            togglePreference(index, elEindex, 'headerLayout')
                          "
                          >Configure</base-button
                        >

                        <base-button
                          @click="
                            deleteElement(index, elEindex, 'headerLayout')
                          "
                          outline
                          danger-invert
                          icon-before="delete"
                          >Delete</base-button
                        >
                      </div>
                    </div>

                    <component
                      :is="dashEl.type"
                      v-if="dashEl.type"
                      v-bind="dashEl.props"
                      :css-class="dashEl.cssClass"
                      @michael="getClassName"

                    />
                    <div
                      v-else-if="!dashEl.type && editMode"
                      :class="[
                        { 'dashboard--add-el': !dashEl.type && editMode },
                      ]"
                    >
                      <base-button
                        @click="
                          toggleAddWidget(index, elEindex, 'headerLayout')
                        "
                        :primary="true"
                      >
                        Add widget
                      </base-button>
                    </div>
                  </div>
                </transition-group>
              </draggable>
              <draggable
                class="row mb-12"
                @start="dragging = true"
                @end="dragging = false"
                :disabled="!editMode"
                v-else
              >
                <div class="col-xs-12 dashboard--add-el">
                  <base-button
                    @click="toggleAddWidget(index, 0, 'headerLayout')"
                    :primary="true"
                    >Add widget</base-button
                  >
                </div>
              </draggable>
            </div>
          </transition-group>
        </draggable>
      </div>
    </section>

    <section class="dropzone dropzone--main">
      <div v-if="noElements && !editMode" class="u-ta-center">
        <h3>Your dashboard is empty</h3>
        <p>You can personalise your dashboard by adding new widgets.</p>
        <p>
          <base-button @click="toggleCustomize" primary="true"
            >Click here to customise now</base-button
          >
        </p>
      </div>
      <div class="dropzone__container">
        <draggable
          class="dashboard--dynamic__row"
          @start="dragging = true"
          @end="dragging = false"
          :disabled="!editMode"
          handle=".handle"
          group="mainRows"
          v-model="mainLayout"
          v-bind="dragOptions"
        >
          <transition-group
            type="transition"
            :name="!dragging ? 'flip-list' : null"
          >
            <div v-for="(row, index) in mainLayout" :key="index">
              <div v-if="editMode" slot="header">
                <span class="handle cursor-pointer"
                  ><icon width="16" height="16" icon="move" /> Move row</span
                >
                <base-button
                  ghost
                  inline
                  icon-before="element-group"
                  @click="toggleColumnWidth(index, 'mainLayout')"
                  v-if="hasNoAdminElements(index, 'mainLayout')"
                  >Change row layout</base-button
                >
                <base-button
                  @click="deleteRow(index, 'mainLayout')"
                  ghost
                  inline
                  icon-before="delete"
                  v-if="hasNoAdminElements(index, 'mainLayout')"
                  >Delete row</base-button
                >
              </div>

              <draggable
                @start="dragging = true"
                @end="dragging = false"
                :disabled="!editMode"
                v-if="row.length"
                :group="`els${index}`"
                handle=".handle-el"
                v-model="mainLayout[index]"
                v-bind="dragOptions"
              >
                <transition-group
                  type="transition"
                  :name="!dragging ? 'flip-list' : null"
                  class="row mb-12"
                >
                  <div
                    v-for="(dashEl, elEindex) in row"
                    :key="elEindex"
                    :class="`col-xs-12 col-md-${dashEl.cssClass} relative`"
                  >
                    <div
                      v-if="editMode && dashEl.type"
                      class="dashboard__overlay"
                    >
                      <div class="dashboard__overlay__controls">
                        <base-button
                          outline
                          success-invert
                          icon-before="move-side"
                          class="handle-el"
                          v-if="notSingleCol(index, 'mainLayout')"
                          >Move</base-button
                        >
                        <base-button
                          primary-invert
                          icon-before="cog"
                          @click="
                            togglePreference(index, elEindex, 'mainLayout')
                          "
                          v-if="!dashEl.admin"
                          >Configure</base-button
                        >

                        <base-button
                          @click="deleteElement(index, elEindex, 'mainLayout')"
                          outline
                          danger-invert
                          icon-before="delete"
                          v-if="!dashEl.admin"
                          >Delete</base-button
                        >
                      </div>
                    </div>
                    <component
                      :is="dashEl.type"
                      v-if="dashEl.type"
                      v-bind="dashEl.props"
                      :css-class="dashEl.cssClass"
                    />
                    <div
                      v-else-if="!dashEl.type && editMode"
                      :class="[
                        { 'dashboard--add-el': !dashEl.type && editMode },
                      ]"
                    >
                      <base-button
                        @click="toggleAddWidget(index, elEindex, 'mainLayout')"
                        :primary="true"
                      >
                        Add widget
                      </base-button>
                    </div>
                  </div>
                </transition-group>
              </draggable>
              <draggable
                class="row mb-12"
                @start="dragging = true"
                @end="dragging = false"
                :disabled="!editMode"
                v-else
              >
                <div class="col-xs-12 dashboard--add-el">
                  <base-button
                    @click="toggleAddWidget(index, 0, 'mainLayout')"
                    :primary="true"
                    >Add widget</base-button
                  >
                </div>
              </draggable>
            </div>
          </transition-group>
        </draggable>
        <div class="row dashboard--add-el mb-12" v-if="editMode">
          <base-button :primary="true" @click="addRow('mainLayout')"
            >Add new row</base-button
          >
        </div>
      </div>
    </section>

    <section class="dropzone dropzone--footer">
      <div class="dropzone__container">
        <draggable
          class="dashboard--dynamic__row"
          @start="dragging = true"
          @end="dragging = false"
          :disabled="!editMode"
          handle=".handle"
          group="footerRows"
          v-model="footerLayout"
          v-bind="dragOptions"
        >
          <transition-group
            type="transition"
            :name="!dragging ? 'flip-list' : null"
          >
            <div v-for="(row, index) in footerLayout" :key="index">
              <div v-if="editMode" slot="header">
                <base-button
                  @click="deleteRow(index, 'footerLayout')"
                  ghost
                  inline
                  icon-before="delete"
                  v-if="hasNoAdminElements(index, 'footerLayout')"
                  >Delete row</base-button
                >
              </div>
              <draggable
                @start="dragging = true"
                @end="dragging = false"
                :disabled="!editMode"
                v-if="row.length"
                :group="`els${index}`"
                handle=".handle-el"
                v-model="footerLayout[index]"
                v-bind="dragOptions"
              >
                <transition-group
                  type="transition"
                  :name="!dragging ? 'flip-list' : null"
                  class="row mb-12"
                >
                  <div
                    v-for="(dashEl, elEindex) in row"
                    :key="elEindex"
                    :class="`col-xs-12 col-md-${dashEl.cssClass} relative`"
                  >
                    <div
                      v-if="editMode && dashEl.type"
                      class="dashboard__overlay"
                    >
                      <h2 class="text-white" v-if="dashEl.admin">
                        Widget can not be configured
                      </h2>
                      <div class="dashboard__overlay__controls" v-else>
                        <base-button
                          outline
                          success-invert
                          icon-before="move-side"
                          class="handle-el"
                          v-if="notSingleCol(index, 'footerLayout')"
                          >Move</base-button
                        >
                        <base-button
                          primary-invert
                          icon-before="cog"
                          @click="
                            togglePreference(index, elEindex, 'footerLayout')
                          "
                          >Configure</base-button
                        >

                        <base-button
                          @click="
                            deleteElement(index, elEindex, 'footerLayout')
                          "
                          outline
                          danger-invert
                          icon-before="delete"
                          >Delete</base-button
                        >
                      </div>
                    </div>
                    <component
                      :is="dashEl.type"
                      v-if="dashEl.type"
                      v-bind="dashEl.props"
                      :css-class="dashEl.cssClass"
                    />
                    <div
                      v-else-if="!dashEl.type && editMode"
                      :class="[
                        { 'dashboard--add-el': !dashEl.type && editMode },
                      ]"
                    >
                      <base-button
                        @click="
                          toggleAddWidget(index, elEindex, 'footerLayout')
                        "
                        :primary="true"
                      >
                        Add widget
                      </base-button>
                    </div>
                  </div>
                </transition-group>
              </draggable>
              <draggable
                class="row mb-12"
                @start="dragging = true"
                @end="dragging = false"
                :disabled="!editMode"
                v-else
              >
                <div class="col-xs-12 dashboard--add-el">
                  <base-button
                    @click="toggleAddWidget(index, 0, 'footerLayout')"
                    :primary="true"
                    >Add widget</base-button
                  >
                </div>
              </draggable>
            </div>
          </transition-group>
        </draggable>
      </div>
    </section>

    <dashboard-controls
      @save-layout="saveLayout"
      @toggle-customize="toggleCustomize"
      :edit-mode="editMode"
      v-if="!$adminDisabled"
      class="u-hide-mobile"
    />

    <modal v-if="showTakeover && !takeoverHidden">
      <template slot="header">
        <h3>{{ takeover.title }}</h3>
      </template>
      <p>{{ takeover.description }}</p>
      <template slot="footer">
        <base-button :primary="true" @click="hideTakeover"
          >OK, got it</base-button
        >
      </template>
    </modal>

    <modal v-if="showColumnWidthModal">
      <template slot="header">
        <h3>Set column width</h3>
      </template>
      <p>Select how many columns you want to display</p>
      <div class="flex space-even">
        <button
          v-for="i in 3"
          :key="i"
          @click="setColumnCount(i)"
          class="btn--column"
        >
          <div :class="`column column--${i}`"></div>
          <span>{{ i }} column{{ i > 1 ? "s" : "" }}</span>
        </button>
      </div>
      <template slot="footer">
        <base-button danger @click="toggleColumnWidth">Cancel</base-button>
      </template>
    </modal>

    <modal v-if="showWidgetModal">
      <template slot="header">
        <h3>Select widget</h3>
      </template>
      <p>Choose a widget to add</p>
      <div class="flex space-even flex-wrap add-module">
        <button
          v-for="(mod, index) in modules"
          :key="index"
          @click="addWidget(mod)"
          class="btn--column"
        >
          <div>
            <icon :icon="getIcon(mod)" width="30" height="30" :fill="false" />
          </div>
          <span>{{ mod }}</span>
        </button>
      </div>
      <template slot="footer">
        <base-button danger @click="toggleAddWidget">Cancel</base-button>
      </template>
    </modal>

    <modal v-if="showConfigureWidget">
      <template slot="header">
        <h3>Configure {{ activeWidgetType | titleCase }} Widget</h3>
      </template>
      <div
        class="flex"
        v-for="prop in activeWidget.propsConfig"
        :key="prop.key"
      >
        <form-field
          full-width
          :label="prop.label | titleCase"
          :name="prop.key"
          type="text"
          :value="newProps[prop.key]"
          v-model="newProps[prop.key]"
          v-if="prop.type == 'text'"
          :helper-text="prop.helperText"
          v-validate.initial="prop.validation"
          :error="errors.first(prop.key)"
        ></form-field>
        <form-field
          full-width
          :label="prop.label | titleCase"
          :name="prop.key"
          :value="newProps[prop.key]"
          v-model="newProps[prop.key]"
          v-else-if="prop.type == 'checkbox'"
          :helper-text="prop.helperText"
          v-validate.initial="prop.validation"
          :error="errors.first(prop.key)"
        >
          <div v-for="(val, index) in prop.options" :key="index">
            <label :for="`checkbox_${index}`"
              ><input
                type="checkbox"
                :checked="newProps[prop.key].includes(val.value)"
                @change="updateContentType(prop.key, val.value)"
                :id="`checkbox_${index}`"
                :name="`${activeWidgetType}_${prop.key}`"
              />
              {{ val.name }}</label
            >
          </div>
        </form-field>
      </div>
      <message v-if="configSubmitted && errors.items.length > 0" type="danger">
        <p>
          There was problems submitting your form, please look at the
          highlighted fields above.
        </p>
      </message>
      <template slot="footer">
        <base-button :primary="true" @click="saveProp">Save</base-button>
        <base-button danger @click="togglePreference">Cancel</base-button>
      </template>
    </modal>
  </div>
</template>

<script>
/**
 * Need to change how this works now:
 *
 * All elements now need to mirror the behavior of the main widget section
 * This means that it needs to pull through admin elements for header, main and footer sections
 * And now users can add elements to all sections
 * So basically the behaviour of the main section needs to be replicated to the header and footer section
 */
import draggable from 'vuedraggable';

import { BaseContainer } from '@/components/Containers';
import { mapState, mapGetters } from 'vuex';
import { modules } from '@/components/widgets';
import DashboardControls from '@/components/DashboardControls';
import UserHasPermission from '@/mixins/UserHasPermission';
import WidgetMixin from '@/mixins/dashboard';
import AnnouncementWidget from '@/components/widget-announcement';

const SystemIcon = () => import('@/components/SystemIcon');

export default {
  name: 'dashboard',
  components: {
    BaseContainer,
    SystemIcon,
    draggable,
    DashboardControls,
    AnnouncementWidget,
  },
  mixins: [UserHasPermission, WidgetMixin],
  data() {
    return {
      dragging: false,
      comClassName: '',
      loading: true,
      takeoverVisible: !localStorage.getItem('hideTakeover'),
      takeoverHidden: false,
      modules,
      table: {
        fields: [
          {
            name: 'system-status',
            title: 'Status',
            titleClass: 'u-ta-center',
            dataClass: 'u-ta-center',
            width: '10%',
          },
          {
            name: 'name',
            title: 'System',
            titleClass: 'u-ta-left',
            width: '15%',
          },
          {
            name: 'actions',
            title: 'Actions',
            titleClass: 'u-ta-center',
            dataClass: 'u-ta-center',
            width: '10%',
          },
        ],
      },
      truncateLimit: 100,
    };
  },
  computed: {
    ...mapGetters('systems', ['orderedSystems']),

    affectedSystems() {
      if (this.orderedSystems) {
        const red = this.orderedSystems.filter(system => system.value === 0);
        const amber = this.orderedSystems.filter(system => system.value === 2);

        return [...red, ...amber];
      }
    },
    showTakeover() {
      if (!this.takeover) {
        return false;
      }
      if (this.takeover.persistent && !this.takeoverHidden) {
        return true;
      }

      return this.takeoverVisible;
    },
    takeover() {
      return this.$store.state.takeover[0];
    },
  },
  beforeMount() {
    // need to clear search query for systems
    this.$store.commit('systems/setSearchQuery', '');
  },
  async mounted() {
    this.$store.dispatch('getTakeover');

    /**
     * Do we want to force systems to be updated?
     */
    try {
      await this.$store.dispatch('widgets/getUserDashboard');
    } catch (err) {}
    try {
      await this.$store.dispatch('systems/list', { forceUpdate: true });
      await this.$store.dispatch('widgets/getAdminUserDashboard');
      this.loading = false;
    } catch (err) {
      this.loading = false;
    }

    this.headerLayout = this.$store.getters['widgets/mergedHeaderWidgets'];
    this.mainLayout = this.$store.getters['widgets/mergedMainWidgets'];
    this.footerLayout = this.$store.getters['widgets/mergedFooterWidgets'];
  },
  methods: {
    // actions for following / unfollowing systems
    getClassName(value) {
      console.log(value); // This is an announcement dont you know
      this.comClassName = value;
    },
    onClickFollow(systemId) {
      this.$store.dispatch('systems/favourite', systemId);
      this.$store.commit('systems/SET_EXTERNAL_SYSTEMS', []);
      this.$store
        .dispatch('systems/listExternal', { forceUpdate: true })
        .then((resp) => {
          this.loading = false;
        });
    },
    onClickUnfollow(systemId) {
      this.$store.dispatch('systems/unfavourite', systemId);
      this.$store.commit('systems/SET_EXTERNAL_SYSTEMS', []);
      this.$store
        .dispatch('systems/listExternal', { forceUpdate: true })
        .then((resp) => {
          this.loading = false;
        });
    },
    onStatusClick(row) {
      if (
        !this.$route.meta.isAdmin &&
        (row.event.target.localName === 'td' ||
          row.event.target.parentNode.localName === 'td')
      ) {
        this.$router.push({ name: 'ViewStatus', params: { id: row.data.id } });
      }
    },
    hideTakeover() {
      localStorage.setItem('hideTakeover', true);
      this.takeoverHidden = true;
      this.takeoverVisible = false;
    },
  },
  watch: {
    takeover: {
      immediate: true,
      deep: true,
      handler(newValue, oldValue) {
        if (newValue) {
          if (newValue.persistent && !this.takeoverHidden) {
            this.showTakeover = true;
          }
          this.showTakeover = this.takeoverVisible;
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.relative {
  position: relative;
}
.p-dashboard {
  @media (min-width: 768px) {
    width: 95%;
  }
}

/deep/ .c-data-table tbody td {
  display: flex;
  width: 100vw !important;

  @media (min-width: 768px) {
    display: table-cell;
    width: inherit !important;
  }
}
/deep/ .favourites {
  &--inactive {
    svg > path {
      stroke: #425563;
      fill: #425563;
    }
  }

  &--active {
    svg > path {
      stroke: #005eb8;
      fill: #005eb8;
    }
  }
}

.dashboard {
  &--add-el {
    background-image: linear-gradient(
      135deg,
      lighten(#425563, 50%) 25%,
      transparent 25%,
      transparent 50%,
      lighten(#425563, 50%) 50%,
      lighten(#425563, 50%) 75%,
      transparent 75%,
      transparent 100%
    );
    background-repeat: repeat;
    background-size: 12.49px 12.49px;
    border: 3px dashed #425563;
    box-sizing: border-box;
    display: flex;
    margin: 0 0 1vmax;
    padding: 3vh 0;
    place-content: center;
  }

  &__overlay {
    &:before {
      background: #425563;
      color: white;
      content: "";
      display: block;
      height: 100%;
      left: 0;
      opacity: 0.5;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: -1;
    }
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    left: 1rem;
    place-content: center;
    position: absolute;
    top: 0;
    width: calc(100% - 2rem);
    z-index: 2;

    &__controls {
      align-items: center;
      display: flex;
      justify-content: center;
      width: 100%;
    }
  }
}
.flip-list-move {
  transition: transform 0.5s;
  @media (prefers-reduced-motion: none) {
    transition: none;
  }
}
.ghost {
  opacity: 0.5;
}

.btn {
  &--column {
    background: none;
    border: 3px solid $colour-nhs-med-grey;
    border-radius: 20px;
    cursor: pointer;
    margin: 0 2.5% 1rem;
    padding: 20px;
    text-transform: capitalize;
    min-width: 28%;
    flex: 1 1 0;

    div {
      margin-bottom: 10px;
    }

    &:hover {
      border-color: $colour-nhs-core-blue;
    }
  }
}

.column {
  background: $colour-nhs-med-grey;

  height: 100px;
  margin: 0 auto 10px;
  position: relative;
  width: 100%;
  &--2 {
    &:before {
      width: 1px;
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      height: 100%;
      background: white;
      content: "";
    }
  }
  &--3 {
    &:before,
    &:after {
      width: 1px;
      position: absolute;
      top: 0;
      transform: translateX(-50%);
      height: 100%;
      background: white;
    }
    &:before {
      left: 33%;
      content: "";
    }
    &:after {
      width: 1px;
      position: absolute;
      top: 0;
      left: 66%;
      transform: translateX(-50%);
      height: 100%;
      background: white;
      content: "";
    }
  }
}
.add-module {
  /deep/ .svg-icon {
    stroke: #425563;
  }
}

/deep/ .c-section-block {
  @media (min-width: 768px) {
    margin-bottom: 0;
  }
}
.mb-12 {
  margin-bottom: 3rem;
}

.justify-between {
  display: flex;
  align-items: center;
  justify-content: space-evenly;
}
</style>
